import { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
const token = () => localStorage.getItem("adminToken");

function RoleBadge({ role }) {
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${
      role === "admin" ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"
    }`}>
      {role === "admin" ? "👑 Admin" : "🛠️ Store Manager"}
    </span>
  );
}

function StatusBadge({ is_active }) {
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
      is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
    }`}>
      {is_active ? "● Active" : "○ Inactive"}
    </span>
  );
}

// ── Add Staff Modal ───────────────────────────────────────────
function AddStaffModal({ onClose, onAdded }) {
  const [form,    setForm]    = useState({ name: "", email: "", password: "", role: "store_manager" });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res  = await fetch(`${API}/api/admin/staff`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      onAdded(data);
      onClose();
    } catch {
      setError("Could not connect to server.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Add Staff Member</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input required value={form.name} onChange={e => set("name", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
              placeholder="John Smith" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input required type="email" value={form.email} onChange={e => set("email", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
              placeholder="john@sagarfashion.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input required type="password" value={form.password} onChange={e => set("password", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
              placeholder="Min 6 characters" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select value={form.role} onChange={e => set("role", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
              <option value="store_manager">Store Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-bold transition">
              {loading ? "Adding..." : "Add Staff"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Reset Password Modal ──────────────────────────────────────
function ResetPasswordModal({ staff, onClose }) {
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 6)  { setError("Password must be at least 6 characters."); return; }
    setError(""); setLoading(true);
    try {
      const res  = await fetch(`${API}/api/admin/staff/${staff.id}/reset-password`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify({ newPassword: password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch {
      setError("Could not connect to server.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-gray-900">Reset Password</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <p className="text-sm text-gray-500 mb-5">
          Resetting password for <span className="font-semibold text-gray-800">{staff.name}</span>
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 text-sm rounded-lg px-4 py-3 mb-4">✓ Password reset successfully!</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input required type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
              placeholder="Min 6 characters" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input required type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
              placeholder="Repeat new password" />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-xs text-amber-700 font-semibold">⚠️ Important</p>
            <p className="text-xs text-amber-600 mt-0.5">The staff member will need to use this new password to login. Their current session will expire within 8 hours.</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={loading || success}
              className="flex-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-bold transition">
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Staff Card ────────────────────────────────────────────────
function StaffCard({ member, onToggleActive, onRoleChange, onResetPassword }) {
  const [changingRole,   setChangingRole]   = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  async function handleToggleActive() {
    setTogglingActive(true);
    try {
      const res = await fetch(`${API}/api/admin/staff/${member.id}/toggle-active`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify({ is_active: !member.is_active }),
      });
      if (res.ok) onToggleActive(member.id, !member.is_active);
    } catch {
      alert("Failed to update status.");
    } finally {
      setTogglingActive(false);
    }
  }

  async function handleRoleChange(newRole) {
    setChangingRole(true);
    try {
      const res = await fetch(`${API}/api/admin/staff/${member.id}/role`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify({ role: newRole }),
      });
      if (res.ok) onRoleChange(member.id, newRole);
    } catch {
      alert("Failed to update role.");
    } finally {
      setChangingRole(false);
    }
  }

  return (
    <div className={`bg-white rounded-xl border shadow-sm p-5 transition ${!member.is_active ? "opacity-60 border-gray-200" : "border-gray-200"}`}>
      <div className="flex items-start justify-between gap-4">

        {/* Avatar + Info */}
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0 ${member.role === "admin" ? "bg-rose-500" : "bg-blue-500"}`}>
            {member.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{member.name}</p>
            <p className="text-sm text-gray-500">{member.email}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Joined {new Date(member.created_at).toLocaleDateString()}
              {member.last_login && ` · Last login ${new Date(member.last_login).toLocaleDateString()}`}
            </p>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <RoleBadge role={member.role} />
          <StatusBadge is_active={member.is_active} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">

        {/* Toggle Active */}
        <button
          onClick={handleToggleActive}
          disabled={togglingActive}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50 ${
            member.is_active
              ? "bg-red-50 text-red-600 hover:bg-red-100"
              : "bg-green-50 text-green-600 hover:bg-green-100"
          }`}>
          {togglingActive ? "..." : member.is_active ? "🔴 Deactivate" : "🟢 Activate"}
        </button>

        {/* Change Role */}
        <button
          onClick={() => handleRoleChange(member.role === "admin" ? "store_manager" : "admin")}
          disabled={changingRole}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-50 text-purple-600 hover:bg-purple-100 transition disabled:opacity-50">
          {changingRole ? "..." : member.role === "admin" ? "↓ Make Store Manager" : "↑ Make Admin"}
        </button>

        {/* Reset Password */}
        <button
          onClick={() => onResetPassword(member)}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-600 hover:bg-amber-100 transition">
          🔑 Reset Password
        </button>
      </div>
    </div>
  );
}

// ── Main Staff Page ───────────────────────────────────────────
export default function Staff() {
  const [staff,          setStaff]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState("");
  const [showAddModal,   setShowAddModal]   = useState(false);
  const [resetTarget,    setResetTarget]    = useState(null);
  const [search,         setSearch]         = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res  = await fetch(`${API}/api/admin/staff`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const data = await res.json();
        if (!cancelled) setStaff(data);
      } catch {
        if (!cancelled) setError("Failed to load staff.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  function onAdded(newMember) {
    setStaff(s => [newMember, ...s]);
  }

  function onToggleActive(id, is_active) {
    setStaff(s => s.map(m => m.id === id ? { ...m, is_active } : m));
  }

  function onRoleChange(id, role) {
    setStaff(s => s.map(m => m.id === id ? { ...m, role } : m));
  }

  const filtered = staff.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.email.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount   = staff.filter(m => m.is_active).length;
  const inactiveCount = staff.filter(m => !m.is_active).length;

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-400 text-sm animate-pulse">Loading staff...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff & Permissions</h1>
          <p className="text-gray-500 text-sm mt-1">
            {activeCount} active · {inactiveCount} inactive
          </p>
        </div>
        <button onClick={() => setShowAddModal(true)}
          className="bg-rose-600 hover:bg-rose-500 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition">
          + Add Staff
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

      {/* Security Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-6">
        <p className="text-sm font-semibold text-amber-800 mb-1">🔐 Security Info</p>
        <p className="text-xs text-amber-700">
          Store manager sessions expire after <strong>8 hours</strong>. 
          Deactivating a staff member immediately blocks all future logins. 
          Active sessions expire naturally within 8 hours after deactivation.
          Reset their password to force them out immediately on next login attempt.
        </p>
      </div>

      {/* Search */}
      <input
        value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search staff by name or email..."
        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-rose-400 mb-5"
      />

      {/* Staff List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-4xl mb-3">👥</p>
          <p className="text-sm">No staff members found.</p>
          <button onClick={() => setShowAddModal(true)} className="text-rose-500 text-sm font-medium mt-2 hover:underline">
            Add your first staff member
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(member => (
            <StaffCard
              key={member.id}
              member={member}
              onToggleActive={onToggleActive}
              onRoleChange={onRoleChange}
              onResetPassword={setResetTarget}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddStaffModal
          onClose={() => setShowAddModal(false)}
          onAdded={onAdded}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          staff={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
    </div>
  );
}